import {
  useConnectHarnessCredential,
  useHarnessCredentials,
  useRevokeHarnessCredential,
} from "@/api/harnessCredentials";
import {
  chipButtonClassName,
  chipDangerButtonClassName,
  GroupLabel,
  LcBadge,
  NESTED_DIALOG_Z,
  PaneBody,
  paneFieldInputClassName,
  Row,
  smButtonClassName,
} from "@/components/AdminPanel/Organization/components/manage-pane-primitives";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/utils";
import {
  HarnessCredential,
  HarnessCredentialKind,
  HarnessCredentialProvider,
} from "@cohesive/api-client";
import { Spinner } from "@cohesive/ui-components";
import { formatDistanceToNow } from "date-fns";
import { Bot, Sparkles } from "lucide-react";
import React, { useMemo, useState } from "react";
import toast from "react-hot-toast";

/**
 * Model providers — whose provider bill an agent turn is spent against.
 *
 * The roster's shape, one row per provider rather than one per credential:
 * only one credential is ever in force for a provider, and which one is the
 * point (yours beats your organization's, and both beat Cohesive's). A row
 * therefore reads as an answer to "who pays for this", with the connect and
 * disconnect verbs hung off it.
 */

const PROVIDERS: {
  id: HarnessCredentialProvider;
  name: string;
  icon: typeof Bot;
  /** Kinds this provider offers. Only Anthropic has a subscription today. */
  kinds: HarnessCredentialKind[];
  placeholder: string;
}[] = [
  {
    id: "anthropic",
    name: "Anthropic",
    icon: Sparkles,
    kinds: ["api_key", "subscription"],
    placeholder: "sk-ant-…",
  },
  {
    id: "openai",
    name: "OpenAI",
    icon: Bot,
    kinds: ["api_key"],
    placeholder: "sk-proj-…",
  },
];

const KIND_LABELS: Record<HarnessCredentialKind, string> = {
  api_key: "API key",
  subscription: "Claude subscription",
};

/**
 * The row's second line: which credential this provider's turns actually spend. Exported because
 * the precedence it describes (yours, then your organization's, then Cohesive's) is the whole
 * point of the pane and worth asserting without a render.
 */
export function describeCredential(
  credential?: HarnessCredential,
  inherited?: HarnessCredential,
): string {
  if (credential?.status === "error") {
    // resolveProvider drops a non-active row, so turns fall through the rest of the precedence
    // exactly as if this one were never connected — say which key that leaves in force, the same
    // way the inherited branch below does. The trailing period goes so a provider-supplied
    // sentence joins cleanly.
    const reason = (
      credential.status_detail ?? "The provider rejected this credential"
    ).replace(/\.$/, "");
    const fallback =
      inherited && inherited.status !== "error"
        ? `your organization's key ending ${inherited.tail}`
        : "Cohesive's key";
    return `${reason} — turns run on ${fallback}`;
  }
  if (credential) {
    return `${KIND_LABELS[credential.kind]} ending ${credential.tail} · connected ${formatDistanceToNow(
      new Date(credential.connected_at),
      { addSuffix: true },
    )}`;
  }
  if (inherited) {
    // resolveProvider drops a non-active row, so an errored org key is not what turns spend.
    return inherited.status === "error"
      ? "Your organization's key was rejected — turns run on Cohesive's key"
      : `Using your organization's key ending ${inherited.tail}`;
  }
  return "Using Cohesive's key";
}

const kindHint = (kind: HarnessCredentialKind) =>
  kind === "subscription"
    ? "Run `claude setup-token` on your own machine and paste the token it prints."
    : "Paste a key from the provider's console. It is checked against the provider before it is stored.";

function ConnectDialog({
  provider,
  open,
  onOpenChange,
}: {
  provider: (typeof PROVIDERS)[number];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const kinds = provider.kinds;
  const [kind, setKind] = useState<HarnessCredentialKind>(kinds[0]);
  const [secret, setSecret] = useState("");
  const connect = useConnectHarnessCredential();

  const submit = async () => {
    try {
      await connect.mutateAsync({
        provider: provider.id,
        kind,
        secret: secret.trim(),
      });
      onOpenChange(false);
      setSecret("");
      toast.success(`${provider.name} connected.`);
    } catch (error) {
      // The connect endpoint makes one live provider call before storing, so a
      // rejected key is reported here rather than mid-turn.
      const message = (
        error as { response?: { data?: { error?: { message?: string } } } }
      )?.response?.data?.error?.message;
      toast.error(
        message || `Failed to connect ${provider.name}. Please try again.`,
      );
    }
  };

  // A connect in flight cannot be cancelled, so dismissing would only hide it: the key still gets
  // stored and swept out to the sandboxes. Hold the dialog until it lands either way.
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !connect.isPending && onOpenChange(next)}
    >
      <DialogContent
        className={cn("sm:max-w-[480px]", NESTED_DIALOG_Z)}
        overlayClassName={NESTED_DIALOG_Z}
      >
        <DialogHeader>
          <DialogTitle>Connect {provider.name}</DialogTitle>
          <DialogDescription>
            Your agent turns run on this instead of your organization&apos;s or
            Cohesive&apos;s credential.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {kinds.length > 1 && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="harness-credential-kind">Credential type</Label>
              <Select
                value={kind}
                onValueChange={(value) =>
                  setKind(value as HarnessCredentialKind)
                }
              >
                <SelectTrigger
                  id="harness-credential-kind"
                  className={paneFieldInputClassName}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={NESTED_DIALOG_Z}>
                  {kinds.map((value) => (
                    <SelectItem key={value} value={value}>
                      {KIND_LABELS[value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="harness-credential-secret">
              {kind === "subscription" ? "Token" : "API key"}
            </Label>
            <Input
              id="harness-credential-secret"
              type="password"
              autoComplete="off"
              spellCheck={false}
              className={paneFieldInputClassName}
              placeholder={provider.placeholder}
              value={secret}
              onChange={(event) => setSecret(event.target.value)}
            />
            <p className="text-sm text-n-50">{kindHint(kind)}</p>
          </div>

          {/* On the form rather than in a footnote: the credential is applied
              by a third party the user did not choose, which sees the plaintext
              of every request it is attached to. */}
          <p className="text-sm text-n-50">
            Cohesive stores this encrypted and never writes it into your
            sandbox. It is applied to outbound requests at the sandbox network
            boundary, which our infrastructure provider operates — they hold the
            credential and see the requests it is attached to.
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            disabled={connect.isPending}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            disabled={!secret.trim() || connect.isPending}
            isLoading={connect.isPending}
            onClick={submit}
          >
            Connect
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RevokeDialog({
  credential,
  providerName,
  open,
  onOpenChange,
}: {
  credential: HarnessCredential;
  providerName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { mutateAsync: revoke, isPending } = useRevokeHarnessCredential();

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !isPending && onOpenChange(next)}
    >
      <DialogContent
        className={cn("sm:max-w-[425px]", NESTED_DIALOG_Z)}
        overlayClassName={NESTED_DIALOG_Z}
      >
        <DialogHeader>
          <DialogTitle>Disconnect {providerName}</DialogTitle>
          <DialogDescription>
            Sandboxes already running stop using it within moments, and later
            turns fall back to your organization&apos;s credential, or
            Cohesive&apos;s. This cannot be undone — reconnecting means pasting
            the credential again.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            disabled={isPending}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            className="bg-spectrum-red text-paper hover:bg-[color-mix(in_oklab,var(--spectrum-red)_88%,var(--ink))]"
            disabled={isPending}
            isLoading={isPending}
            onClick={async () => {
              try {
                await revoke(credential);
                onOpenChange(false);
                toast.success(`${providerName} disconnected.`);
              } catch {
                toast.error(
                  `Failed to disconnect ${providerName}. Please try again.`,
                );
              }
            }}
          >
            Disconnect
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const ModelProviders: React.FC = () => {
  const { data: credentials, isLoading, isError } = useHarnessCredentials();
  const [connecting, setConnecting] = useState<
    (typeof PROVIDERS)[number] | null
  >(null);
  const [revoking, setRevoking] = useState<{
    credential: HarnessCredential;
    providerName: string;
  } | null>(null);

  /**
   * The user endpoint returns both scopes. Connecting is user-scoped only for now, but an org
   * credential still has to be read: it is what a member without their own key spends, and a row
   * that did not name it would read as "you are on Cohesive's key".
   */
  const byScope = useMemo(() => {
    const map = new Map<string, HarnessCredential>();
    for (const credential of credentials ?? []) {
      map.set(`${credential.scope}:${credential.provider}`, credential);
    }
    return map;
  }, [credentials]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    );
  }

  // Without this a failed list renders every provider as unconnected, which reads as "you are on
  // Cohesive's key" — the one thing this pane exists to state.
  if (isError) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-n-50">
        Could not load model providers. Refresh to try again.
      </div>
    );
  }

  const providerRow = (provider: (typeof PROVIDERS)[number]) => {
    const Icon = provider.icon;
    const credential = byScope.get(`user:${provider.id}`);
    const inherited = byScope.get(`org:${provider.id}`);
    const hasError = credential?.status === "error";

    return (
      <Row
        key={provider.id}
        tile={<Icon className="size-5" />}
        name={provider.name}
        meta={describeCredential(credential, inherited)}
      >
        {hasError && <LcBadge tone="blocked">Error</LcBadge>}
        {credential ? (
          <>
            <Button
              variant="ghost"
              size="sm"
              className={chipButtonClassName}
              onClick={() => setConnecting(provider)}
            >
              Replace
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={chipDangerButtonClassName}
              onClick={() =>
                setRevoking({ credential, providerName: provider.name })
              }
            >
              Disconnect
            </Button>
          </>
        ) : (
          <Button
            variant="secondary"
            size="sm"
            className={cn("shrink-0", smButtonClassName)}
            onClick={() => setConnecting(provider)}
          >
            Connect
          </Button>
        )}
      </Row>
    );
  };

  return (
    <PaneBody>
      <section className="flex flex-col gap-0.5">
        <GroupLabel>Your credentials</GroupLabel>
        {PROVIDERS.map(providerRow)}
      </section>

      {connecting && (
        <ConnectDialog
          open
          provider={connecting}
          onOpenChange={(open) => {
            if (!open) setConnecting(null);
          }}
        />
      )}

      {revoking && (
        <RevokeDialog
          open
          credential={revoking.credential}
          providerName={revoking.providerName}
          onOpenChange={(open) => {
            if (!open) setRevoking(null);
          }}
        />
      )}
    </PaneBody>
  );
};

export default ModelProviders;
